import { Link } from "wouter";
import { ArrowRight, Zap, Shield, Clock, Target, ChevronDown, Phone } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import Layout from "@/components/Layout";
import SEOHead, { createOrganizationSchema, createWebSiteSchema } from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import CountUp from "@/components/CountUp";
import { IMAGES } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";
import FaqItem from "@/components/FaqItem";
import { blogPosts } from "@/lib/blogData";
import { Calendar, Newspaper } from "lucide-react";

const stats = [
  { value: "0,1 mm", label: "Tolerans", desc: "Garanterad precision" },
  { value: "25 mm", label: "Max tjocklek", desc: "Svart stål" },
  { value: "1993", label: "Grundat", desc: "30+ års erfarenhet" },
  { value: "<24h", label: "Leverans", desc: "Enkla projekt" },
];

const services = [
  {
    title: "Laserskärning",
    desc: "Hög precision – laserskärning med toleranser ner till 0,1 mm för perfekt passform. Snabba leveranser – vi strävar efter att fullfölja leveranser snabbt, enkla detaljer skickas ofta samma dag eller dagen efter. Brett materialutbud – stål, rostfritt, aluminium och höghållfasthetsstål från SSAB, anpassat för krävande branscher.",
    href: "/tjanster/laserskarning",
    image: IMAGES.heroLaser,
  },
  {
    title: "Kantpressning",
    desc: "Med tre kantpressar upp till 310 ton från Ursviken och Amada bockar vi plåt med hög precision. Från prototyper till stora serier, alltid med fokus på passform.",
    href: "/tjanster/kantpressning",
    image: IMAGES.workshop3,
  },
  {
    title: "Svetsning",
    desc: "Starka och hållbara fogar – svetsning utförd med hög precision för att klara både industriella krav och tuffa miljöer. Flexibilitet i material och metoder – vi hanterar stål, rostfritt, aluminium och specialstål. Del av helhetslösning – kombineras ofta med laserskärning och kantpressning för kompletta komponenter och prototyper.",
    href: "/tjanster/svetsning",
    image: IMAGES.svetsningOldSite,
  },
];

const faqs = [
  {
    q: "Vad kan ni laserskära för material?",
    a: "Aspab kan laserskära ett brett spektrum av material, med särskild expertis inom: svart stål (upp till 25 mm tjocklek), rostfritt stål (upp till 20 mm), aluminium, höghållfasthetsstål som Strenx 650, 700 samt Hardox/Raex, och specialstål för exempelvis jordbruksmaskiner och försvarsapplikationer. Materialvalet anpassas efter krav på hållfasthet, kostnad, skärbarhet och slutapplikation."
  },
  {
    q: "Hur snabbt kan ni leverera?",
    a: "Vårt mål är alltid snabba leveranser, beroende på komplexiteten varierar detta såklart. Men för enklare laserskurna delar erbjuder vi leverans samma dag eller dagen efter. Mer komplexa projekt levereras normalt inom två arbetsdagar."
  },
  {
    q: "Kan ni även hjälpa till med svetsning?",
    a: "Ja, vi erbjuder även svetsning som en del av helhetslösningar vid behov. Många projekt inkluderar skärning, bockning och svetsning innan leverans."
  },
  {
    q: "Är ni certifierade?",
    a: "Vi är just nu mitt uppe i certifieringsprocessen för ISO 9001 (kvalitet), ISO 14001 (miljö) och ISO 45001 (arbetsmiljö). Dessa säkerställer att vi uppfyller högt ställda krav från branscher som försvar, fordon och bygg."
  },
  {
    q: "Vilka branscher jobbar ni med?",
    a: "Vi arbetar med kunder inom fordonsindustrin, försvarssektorn, byggindustrin, jordbrukssektorn och tillverkande industri. Våra lösningar används i allt från prototyper till storserieproduktion och reparationskomponenter."
  },
  {
    q: "Vilka toleranser kan ni uppnå?",
    a: "Vi garanterar toleranser på en tiondel (0,1 mm). Vi är mycket noggranna med att skärsnittet ska vara bra och att passformen ska vara optimerad. Varje projekt inleds med en första bit-kontroll."
  },
  {
    q: "Tar ni emot både små och stora serier?",
    a: "Ja, vi hanterar allt från enstaka prototyper till stora serier. Volym påverkar priset positivt, vid över 20 bitar av samma detalj sjunker styckpriset märkbart tack vare fördelade ställtider."
  },
  {
    q: "Kan ni hjälpa till med konstruktion och design?",
    a: "Absolut. Vi tar emot allt från färdiga CAD-ritningar till enkla skisser på papper. Vi har bred erfarenhet av att konstruera och kan ge råd om materialval, designoptimering och tillverkningsbarhet."
  },
];

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const orgSchema = createOrganizationSchema();
  const siteSchema = createWebSiteSchema();

  return (
    <Layout>
      <SEOHead
        title="ASPAB | Laserskärning med hög precision"
        description="Laserskärning, kantpressning och svetsning med 0,1 mm tolerans. Snabba leveranser från Eskilstuna sedan 1993. Begär offert!"
        keywords="laserskärning, kantpressning, svetsning, plåtbearbetning, Eskilstuna, metallbearbetning"
        canonical="https://aspab.com"
        path="/"
        structuredData={[faqSchema, orgSchema, siteSchema]}
        ogImage={IMAGES.ogHem}
      />

      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden xl:pt-14">
        <div className="absolute inset-0">
          {/* Video background - autoplay, muted, loop. Hidden on small screens for performance. */}
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={IMAGES.heroLaser}
            className="w-full h-full object-cover hidden sm:block"
          >
            <source src={IMAGES.laserVideo} type="video/mp4" />
          </video>
          {/* Static image on mobile instead of video */}
          <img
            src={IMAGES.heroLaser}
            alt="Laserskärning med CO2 Laser hos ASPAB i Eskilstuna"
            className="w-full h-full object-cover sm:hidden"
            width={1024}
            height={1024}
            sizes="100vw"
            fetchPriority="high"
            decoding="async"
          />
          {/* Fallback image for browsers that don't support video */}
          <noscript>
            <img
              src={IMAGES.heroLaser}
              alt="Laserskärning med CO2 Laser hos ASPAB i Eskilstuna"
              className="w-full h-full object-cover absolute inset-0"
              width={1920}
              height={1080}
            />
          </noscript>
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent" />
        </div>

        <div className="relative container pt-24 pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-md mb-6">
              <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] animate-pulse" />
              <span className="text-white text-sm font-medium">Metallbearbetning i Eskilstuna sedan 1993</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05]">
              Laserskärning med{" "}
              <span className="text-[#60A5FA]">hög precision</span>{" "}
              i världsklass
            </h1>

            <ul className="mt-6 space-y-2">
              <li className="flex items-center gap-2 text-gray-200 text-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-[#60A5FA]" />
                Modern teknik och gedigen erfarenhet
              </li>
              <li className="flex items-center gap-2 text-gray-200 text-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-[#60A5FA]" />
                Laserskärning i stål, rostfritt, aluminium samt mässing
              </li>
              <li className="flex items-center gap-2 text-gray-200 text-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-[#60A5FA]" />
                Noggranna toleranser ner till 0,1 mm
              </li>
            </ul>

            <p className="mt-5 text-gray-300 max-w-xl leading-relaxed">
              ASPAB erbjuder skräddarsydda tjänster inom laserskärning, kantpressning, svetsning
              samt kompletterande tjänster. Vårt fokus ligger på att leverera precision och kvalitet
              med moderna tekniker i varje projekt.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                href="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors"
              >
                Kontakta oss för personlig rådgivning
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/tjanster"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium rounded-md hover:bg-white/20 transition-colors"
              >
                Plåtbearbetning och tjänster
              </Link>
            </div>

            <p className="mt-4 text-gray-500 text-sm">Vi erbjuder snabba leveranser.</p>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-500">
          <span className="text-xs tracking-widest uppercase">Scrolla</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </section>

      {/* STATS BAR */}
      <section className="relative bg-white border-b border-gray-200">
        <div className="container py-10 md:py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
            {stats.map((stat, i) => (
              <AnimatedSection key={stat.label} delay={i * 100}>
                <div className="text-center lg:text-left">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-[#2563EB]">
                    <CountUp value={stat.value} duration={1800} delay={i * 150} />
                  </div>
                  <div className="text-gray-900 font-medium mt-1">{stat.label}</div>
                  <div className="text-gray-500 text-sm mt-0.5">{stat.desc}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* TRUSTED BY / LEVERERAR TILL LEDANDE VARUMÄRKEN */}
      <section className="bg-white py-16 md:py-20">
        <div className="container">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Betrodda av ledande företag</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  ASPAB levererar till ledande varumärken
                </h2>
                <ul className="mt-6 space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                    <span className="text-gray-600">Leverantör av specialdetaljer till bland annat Volvo Tech Center med flera</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                    <span className="text-gray-600">Tillverkar försvarskomponenter enligt höga säkerhetskrav</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] mt-2 shrink-0" />
                    <span className="text-gray-600">Pågående certifiering på ISO-9001, ISO-14001 samt ISO-45001</span>
                  </li>
                </ul>
                <p className="text-gray-500 mt-6 leading-relaxed">
                  ASPAB samarbetar med flera välkända varumärken och industrijättar världen över.
                  Bland ASPAB:s samarbeten och leverantörsrelationer finns Volvo och SSAB, som ställer höga
                  krav på kvalitet, precision och leveranssäkerhet. Genom modern teknik och djup
                  materialkunskap levererar ASPAB både prototyper och stora serier till dessa aktörer
                  med korta ledtider och hög tillförlitlighet.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-12 md:gap-16">
                {[
                  { name: "Volvo Construction Equipment", src: "/images/xfLmmcXwIvblooWb.webp" },
                  { name: "SSAB", src: "/images/pjrTawDCZYIpzLGT.webp" },
                ].map((brand) => (
                  <div key={brand.name} className="hover:scale-105 transition-transform duration-300">
                    <img
                      src={brand.src}
                      alt={`${brand.name} logotyp - ASPAB kund`}
                      className="h-24 md:h-32 w-auto object-contain"
                      loading="lazy"
                      width={200}
                      height={128}
                    />
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Våra tjänster</span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-3">
                Komplett metallbearbetning under ett tak
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Från skärning till färdig komponent. Vi hanterar hela kedjan med laserskärning,
                kantpressning, svetsning och ytbehandling.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <AnimatedSection key={svc.title} delay={i * 150}>
                <Link href={svc.href} className="group block">
                  <div className="relative overflow-hidden rounded-lg bg-white border border-gray-200 hover:border-[#2563EB]/30 hover:shadow-lg transition-all duration-300">
                    <div className="aspect-[16/10] overflow-hidden">
                      <LazyImage
                        src={svc.image}
                        alt={`${svc.title} hos ASPAB i Eskilstuna`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        wrapperClassName="w-full h-full"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-semibold text-gray-900 group-hover:text-[#2563EB] transition-colors">
                        {svc.title}
                      </h3>
                      <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                        {svc.desc}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-[#2563EB] text-sm font-medium mt-4 group-hover:gap-3 transition-all">
                        Läs mer <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* LASER VIDEO SECTION */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div className="overflow-hidden rounded-lg">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full aspect-video object-cover rounded-lg"
                  poster={IMAGES.heroLaser}
                >
                  <source src={IMAGES.laserVideo} type="video/mp4" />
                </video>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Se oss i aktion</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Tekniskt avancerad maskinpark
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Hos ASPAB finner ni en komplett mekanisk verkstad. Vår maskinpark inkluderar
                  tre CO2-lasrar, tre kantpressar, rörlaser, stansmaskin, borr/fräs och svetsstation.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vi klarar materialtjocklekar bland annat från 0,5 mm upp till 25 mm i svart stål
                  och 20 mm i rostfritt.
                </p>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {[
                    { val: "3", label: "CO2-lasrar" },
                    { val: "3", label: "Kantpressar" },
                    { val: "6 kW", label: "Max lasereffekt" },
                    { val: "310 ton", label: "Max presskraft" },
                  ].map((item) => (
                    <div key={item.label} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="font-heading text-2xl font-bold text-[#2563EB]">{item.val}</div>
                      <div className="text-gray-500 text-sm mt-0.5">{item.label}</div>
                    </div>
                  ))}
                </div>
                <Link
                  href="/maskinpark"
                  className="inline-flex items-center gap-2 text-[#2563EB] font-medium mt-6 hover:gap-3 transition-all"
                >
                  Se hela maskinparken <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* WHY ASPAB */}
      <section className="bg-gray-50 py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Varför ASPAB</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Snabb leverans utan kompromiss med kvaliteten
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Vi har byggt vår verksamhet kring att vara snabba. Enkla projekt levereras samma dag
                  eller dagen efter. Komplexa projekt inom två dagar. Och vi gör det med toleranser
                  som få kan matcha.
                </p>

                <div className="mt-8 space-y-5">
                  {[
                    { icon: Zap, title: "Snabb leverans", text: "Skurna bitar samma dag eller dagen efter. Komplexa projekt inom två dagar." },
                    { icon: Target, title: "Hög precision", text: "Toleranser på en tiondel (0,1 mm) garanterat. Vi är noggranna med passform och skärsnitt." },
                    { icon: Shield, title: "Kvalitetssäkring", text: "Första bit-kontroll vid varje nytt projekt. Stickprov under produktion. Vi löser problem i 99,9% av fallen." },
                    { icon: Clock, title: "Konsultativ approach", text: "Vi tar emot allt från CAD-ritningar till skisser på papper och hjälper er hela vägen till färdig produkt." },
                  ].map(({ icon: Icon, title, text }) => (
                    <div key={title} className="flex gap-4">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                        <Icon className="w-5 h-5 text-[#2563EB]" />
                      </div>
                      <div>
                        <h3 className="text-gray-900 font-semibold">{title}</h3>
                        <p className="text-gray-500 text-sm mt-0.5 leading-relaxed">{text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="relative">
                <LazyImage
                  src={IMAGES.snabbLeverans}
                  alt="Snabb leverans av laserskurna detaljer från ASPAB"
                  className="w-full rounded-lg shadow-lg"
                />
                <div className="absolute -bottom-4 -left-4 bg-[#2563EB] px-6 py-4 rounded-lg shadow-lg">
                  <div className="font-heading text-2xl font-bold text-white">30+</div>
                  <div className="text-white text-sm">års erfarenhet</div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA BAND - from old site */}
      <section className="bg-[#2563EB] py-14 md:py-16">
        <div className="container text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Behöver ni en skräddarsydd lösning?
            </h2>
            <p className="text-white mt-3 text-lg max-w-2xl mx-auto">
              Tveka inte att kontakta oss. Ring eller kontakta oss för personlig rådgivning.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:016132600"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#2563EB] font-semibold rounded-md hover:bg-gray-100 transition-colors"
              >
                <Phone className="w-5 h-5" />
                016-13 26 00
              </a>
              <Link
                href="/kontakt"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-md hover:bg-white/20 transition-colors"
              >
                Kontakta oss <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* NEWS / NYHETER */}
      <section className="bg-white py-16 md:py-20">
        <div className="container">
          <AnimatedSection>
            <div className="flex items-center justify-between mb-10">
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Senaste nytt</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Nyheter från ASPAB
                </h2>
              </div>
              <Link
                href="/nyheter"
                className="hidden sm:inline-flex items-center gap-2 text-[#2563EB] font-medium hover:gap-3 transition-all"
              >
                Alla nyheter <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <Link href={`/nyheter/${blogPosts[0].slug}`} className="group block">
              <div className="grid md:grid-cols-2 gap-8 bg-gray-50 rounded-xl p-6 md:p-8 border border-gray-200 hover:border-[#2563EB]/30 hover:shadow-lg transition-all duration-300">
                <div className="overflow-hidden rounded-lg">
                  <LazyImage
                    src={blogPosts[0].image}
                    alt={blogPosts[0].imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    wrapperClassName="w-full aspect-[4/3]"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-full">
                      {blogPosts[0].category}
                    </span>
                    <span className="flex items-center gap-1.5 text-gray-500 text-xs">
                      <Calendar className="w-3.5 h-3.5" />
                      {blogPosts[0].date}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 group-hover:text-[#2563EB] transition-colors leading-tight">
                    {blogPosts[0].title}
                  </h3>
                  <p className="text-gray-500 mt-4 leading-relaxed">
                    {blogPosts[0].excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-[#2563EB] font-medium mt-6 group-hover:gap-3 transition-all">
                    Läs hela artikeln <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          </AnimatedSection>

          <div className="mt-6 sm:hidden text-center">
            <Link
              href="/nyheter"
              className="inline-flex items-center gap-2 text-[#2563EB] font-medium"
            >
              Alla nyheter <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT ASPAB - from old site */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Teknisk kompetens och erfarenhet</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  ASPAB — Ledande inom laserskärning
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  ASPAB är ett svenskt industriföretag med spetskompetens inom laserskärning,
                  kantpressning och plåtbearbetning. Vi grundades 1993 och drivs sedan 2020 av
                  Robin Fischer med team, som tagit verksamheten till en ny nivå av teknisk förmåga,
                  kvalitet och leveranssäkerhet.
                </p>
                <div className="mt-6 space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <h3 className="text-gray-900 font-semibold">Marknadens modernaste utrustning</h3>
                    <p className="text-gray-500 text-sm mt-1 leading-relaxed">
                      Vi investerar inte enligt scheman – vi investerar när kundernas behov kräver det.
                      På så sätt säkerställer vi att varje investering direkt bidrar till ökad kundnytta.
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <h3 className="text-gray-900 font-semibold">Framgång bygger på människorna bakom maskinerna</h3>
                    <p className="text-gray-500 text-sm mt-1 leading-relaxed">
                      Idag har ASPAB folk och kapacitet både digitalt och ute på verkstadsgolvet. Vi kan
                      hjälpa kunder snabbt, med skurna bitar samma dag eller dagen efter.
                    </p>
                  </div>
                </div>

                <blockquote className="mt-6 pl-5 border-l-2 border-[#2563EB]/50">
                  <p className="text-gray-500 italic leading-relaxed">
                    "Nu kan jag nog ta mig för bröstet och säga att vi faktiskt är snabba. Vi har
                    folk och kapacitet och vi kan hjälpa kunder jäkligt fort."
                  </p>
                  <cite className="text-[#2563EB] text-sm mt-2 block not-italic">Robin Fischer, VD ASPAB</cite>
                </blockquote>

                <Link
                  href="/om-oss"
                  className="inline-flex items-center gap-2 text-[#2563EB] font-medium mt-6 hover:gap-3 transition-all"
                >
                  Läs mer om oss <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <LazyImage
                src={IMAGES.robinFischer}
                alt="Robin Fischer, VD ASPAB"
                className="w-full rounded-lg shadow-lg"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Kundcase</span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-3">
                Så löser vi våra kunders utmaningar
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Från prototyper åt Volvo till storserieproduktion och säkerhetskritiska komponenter
                för försvarsindustrin. Här är några exempel på hur vi arbetar.
              </p>
            </div>
          </AnimatedSection>

          <div className="space-y-8">
            {/* Case 1: Volvo */}
            <AnimatedSection delay={100}>
              <div className="grid lg:grid-cols-5 gap-6 bg-white rounded-xl p-6 md:p-8 border border-gray-200">
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-full">Fordonsindustri</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">Prototyp</span>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-gray-900">
                    Volvo prototyppartnerskap — från idé till verklighet
                  </h3>
                  <p className="text-gray-500 mt-3 leading-relaxed">
                    Volvo vänder sig kontinuerligt till Aspab för sina prototyper och kundanpassningar –
                    från helt nya fordonskoncept till speciallösningar för enskilda kunder. Varje projekt
                    är unikt och kräver problemlösning — ibland får vi en skiss på papper och ska bygga
                    en komplett lösning. Vi använder våra lasrar för att uppnå toleranser på 0,1 mm
                    och ger konstruktionshjälp när specifikationerna är ofullständiga.
                  </p>
                  <blockquote className="mt-4 pl-4 border-l-2 border-[#2563EB]/40 text-gray-600 italic text-sm">
                    "Alla Volvo prototyper och kundanpassningar kommer ju till oss. Volvo väntar på att vi ska
                    få igång rörlasern så att dom kan köpa grejer därifrån."
                    <span className="block mt-1 not-italic text-gray-500">— Robin Fischer, VD</span>
                  </blockquote>
                  <Link href="/referenser/volvo" className="inline-flex items-center gap-1.5 text-[#2563EB] text-sm font-medium mt-4 hover:gap-3 transition-all">
                    Läs hela caset <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
                <div className="lg:col-span-2">
                  <LazyImage src={IMAGES.referensVolvo} alt="Volvo prototyp tillverkad av ASPAB" className="w-full h-full object-cover rounded-lg" wrapperClassName="w-full h-full" />
                </div>
              </div>
            </AnimatedSection>

            {/* Case 2: Försvarsindustrin */}
            <AnimatedSection delay={200}>
              <div className="grid lg:grid-cols-5 gap-6 bg-white rounded-xl p-6 md:p-8 border border-gray-200">
                <div className="lg:col-span-2 order-2 lg:order-1">
                  <LazyImage src={IMAGES.closeup1} alt="Säkerhetskritiska komponenter för försvarsindustrin" className="w-full h-full object-cover rounded-lg" wrapperClassName="w-full h-full" />
                </div>
                <div className="lg:col-span-3 order-1 lg:order-2">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-full">Försvar</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">Säkerhetskritiskt</span>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-gray-900">
                    Försvarsindustrin — skjutbanekomponenter med ballistiska krav
                  </h3>
                  <p className="text-gray-500 mt-3 leading-relaxed">
                    Vi tillverkar komponenter till skjutbanor för försvarssektorn, där säkerhet och prestanda
                    är kritiska. Det kräver djup kunskap om specialstål för ballistiska tillämpningar,
                    rådgivning om ytbehandling för väderbeständighet och extra noggrann kvalitetskontroll.
                  </p>
                  <blockquote className="mt-4 pl-4 border-l-2 border-[#2563EB]/40 text-gray-600 italic text-sm">
                    "Då pratar vi om att jag skulle klara ballistik och då behöver man ju kolla på
                    någonting annat än svart stål."
                    <span className="block mt-1 not-italic text-gray-500">— Robin Fischer, VD</span>
                  </blockquote>
                  <Link href="/referenser/forsvarsindustrin" className="inline-flex items-center gap-1.5 text-[#2563EB] text-sm font-medium mt-4 hover:gap-3 transition-all">
                    Läs hela caset <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>


            {/* Case 4 & 5 side by side */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Case 4: Jordbruksmaskiner */}
              <AnimatedSection delay={400}>
                <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-200 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-full">Jordbruk</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">Slitstål</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-gray-900">
                    Jordbruksmaskiner — slitstål och reparationsmarknaden
                  </h3>
                  <p className="text-gray-500 mt-3 leading-relaxed text-sm">
                    Vi har kontakter inom jordbruksbranschen och ser stor potential inom reparationsmarknaden.
                    Jordbrukskomponenter kräver extremt specialiserat slitstål och höghållfasthetsstål.
                    Vi erbjuder ett kostnadseffektivt alternativ till dyra originaldelar.
                  </p>
                  <blockquote className="mt-4 pl-4 border-l-2 border-[#2563EB]/40 text-gray-600 italic text-sm">
                    "Någonstans där måste ju kanske folk reparera sina grejer. Och då kanske inte
                    vill vända sig till dem för de lär kosta sex gånger mer."
                    <span className="block mt-1 not-italic text-gray-500">— Robin Fischer, VD</span>
                  </blockquote>
                </div>
              </AnimatedSection>

              {/* Case 5: Problemlösning */}
              <AnimatedSection delay={500}>
                <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-200 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-[#2563EB]/10 text-[#2563EB] text-xs font-medium rounded-full">Problemlösning</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">Konsultativt</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-gray-900">
                    Från skiss till flygplan — kreativ problemlösning
                  </h3>
                  <p className="text-gray-500 mt-3 leading-relaxed text-sm">
                    Kunder kommer ofta med ofullständiga specifikationer, från enkla skisser till vaga idéer.
                    Vi utvecklar färdiga produkter genom djupgående kundsamtal, CAD-utveckling och materialoptimering.
                    En kund ville ha "en liten stålbit" — vi identifierade att det behövdes höghållfasthetsstål
                    för en diskharv, inte vanligt svart stål.
                  </p>
                  <blockquote className="mt-4 pl-4 border-l-2 border-[#2563EB]/40 text-gray-600 italic text-sm">
                    "Ibland får vi ju en skiss på ett papper, sen ska vi ja bygga ett flygplan av en skruv."
                    <span className="block mt-1 not-italic text-gray-500">— Robin Fischer, VD</span>
                  </blockquote>
                </div>
              </AnimatedSection>
            </div>
          </div>

          {/* CTA under cases */}
          <AnimatedSection delay={600}>
            <div className="mt-12 text-center">
              <Link
                href="/kontakt"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors"
              >
                Har du ett liknande projekt? Kontakta oss
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mx-auto text-center mb-12">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Vanliga frågor</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Vanliga frågor &amp; svar
              </h2>
            </div>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <FaqItem
                  question={faq.q}
                  answer={faq.a}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
